import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  UseBaseQueryOptions,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { authTokenConfig } from "src/api";
import { caseTemplatesApi } from "src/api/endpoints";
import { deserializeJsonApi } from "src/api/jsonApiHelpers";
import { useCurrentWorkspace } from "src/api/queries";
import { DeserializedCaseTemplateWithVersions } from "src/cases/queues/types";
import {
  CaseBlockConfigOutput,
  JsonApiResourceObjectCaseTemplateAttributesBaseModel as CaseTemplate,
  CaseTemplateJsonSchemaAttributes,
  CaseTemplateJsonSchemaResponse,
  CaseTemplateMetadataUpdate,
  CaseTemplateResponse,
  CaseTemplatesResponse,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion,
  CaseTemplateVersionAttributes,
  CaseTemplateVersionPublishState,
  Data,
} from "src/clients/case-management";
import { CaseCreationNode } from "src/constants/NodeDataTypes";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const STALE_TIME = 1000 * 60 * 5; // 5 minutes

export type TemplateWithVersion = {
  templateId: string;
  versionId: string;
  templateName: string;
  versionName: string;
  icon: string | null | undefined;
  template: CaseTemplate;
  version: CaseTemplateVersion;
};

export const caseTemplatesKeys = {
  all: (workspaceId: string) => ["caseTemplates", workspaceId] as const,
  list: (workspaceId: string) =>
    [...caseTemplatesKeys.all(workspaceId), "list"] as const,
  get: (workspaceId: string, templateId: string) =>
    [...caseTemplatesKeys.all(workspaceId), "template", templateId] as const,
  schema: (workspaceId: string, templateId: string) =>
    [...caseTemplatesKeys.all(workspaceId), "schema", templateId] as const,
  dataSchema: (workspaceId: string, templateId: string, versionId: string) =>
    [
      ...caseTemplatesKeys.all(workspaceId),
      "dataSchema",
      templateId,
      versionId,
    ] as const,
  version: (workspaceId: string, templateId: string, versionId: string) =>
    [
      ...caseTemplatesKeys.all(workspaceId),
      templateId,
      "version",
      versionId,
    ] as const,
};

type CaseTemplateQueryOptions<TData = CaseTemplateResponse> =
  UseBaseQueryOptions<CaseTemplateResponse, AxiosError, TData>;

type CaseTemplateVersionQueryOptions<TData = CaseTemplateVersion> =
  UseBaseQueryOptions<CaseTemplateVersion, AxiosError, TData>;

const useCaseManagementClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  return useMemo(
    () =>
      workspace?.base_url ? caseTemplatesApi(workspace.base_url) : undefined,
    [workspace?.base_url],
  );
};

const useWorkspaceParams = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useCaseManagementClient();

  return { wsId, apiClient };
};

export const useCaseTemplatesRaw = <TData = CaseTemplate>(
  options: Omit<
    UseQueryOptions<CaseTemplatesResponse, AxiosError, TData>,
    "queryKey" | "queryFn"
  > = { enabled: true },
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplatesResponse, AxiosError, TData>({
    queryKey: caseTemplatesKeys.list(wsId),
    queryFn: async () => {
      if (!apiClient) {
        throw new Error(
          "Case management API client is not available. Please check workspace configuration.",
        );
      }

      const response = await apiClient.getCaseTemplatesApiV1TemplatesGet();

      return response.data;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled: !!apiClient && options.enabled,
  });
};

const combineTemplatesWithVersions = (
  templatesResponse: CaseTemplatesResponse | undefined,
): TemplateWithVersion[] => {
  if (!templatesResponse?.data || !templatesResponse?.included) {
    return [];
  }

  return templatesResponse.data.flatMap((template) => {
    const templateVersions = template.relationships?.versions?.data;
    if (!Array.isArray(templateVersions)) return [];

    return templateVersions.flatMap((versionRef) => {
      const version = templatesResponse.included?.find(
        (includedItem) => includedItem.id === versionRef.id,
      );
      if (!version) return [];

      return {
        templateId: template.id,
        versionId: version.id,
        templateName: template.attributes.template_name,
        versionName: version.attributes.version_name,
        icon: template.attributes.icon,
        template,
        version,
      };
    });
  });
};

export const useAllCaseVersionsCombinedWithTemplates = (
  options = {
    enabled: true,
  },
) => {
  return useCaseTemplatesRaw({
    select: combineTemplatesWithVersions,
    enabled: options.enabled,
  });
};

const linkVersionsToTemplates = (data: CaseTemplatesResponse) => {
  return data.data.map((template) => {
    const versionRefs = template.relationships?.versions?.data;
    const templateVersions = Array.isArray(versionRefs)
      ? versionRefs.map((versionRef: Data) => versionRef.id)
      : [];

    return {
      ...template,
      versions: data.included?.filter((included) =>
        templateVersions.includes(included.id),
      ),
    };
  });
};

export type TemplateWithVersions = ReturnType<typeof linkVersionsToTemplates>;

export const useCaseTemplatesWithVersions = (
  options: Omit<
    UseQueryOptions<CaseTemplatesResponse, AxiosError, TemplateWithVersions>,
    "queryKey" | "queryFn" | "select"
  > = {},
) => {
  return useCaseTemplatesRaw({
    select: linkVersionsToTemplates,
    ...options,
  });
};

export const useCaseTemplateSchema = (
  templateId: string | undefined,
  versionId: string | undefined,
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplateJsonSchemaResponse, AxiosError>({
    queryKey: caseTemplatesKeys.schema(wsId, templateId || ""),
    queryFn: async () => {
      if (!apiClient || !templateId || !versionId) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.getCaseTemplateVersionInputJsonSchemaApiV1TemplatesTemplateIdVersionsVersionIdCaseJsonSchemaGet(
          { templateId, versionId },
        );
      return response.data;
    },
    staleTime: STALE_TIME,
    enabled: !!apiClient && !!templateId && !!versionId,
  });
};

export const useCaseTemplateDataSchema = (
  templateId: string | undefined,
  versionId: string | undefined,
  options?: Omit<
    UseQueryOptions<CaseTemplateJsonSchemaResponse, AxiosError>,
    "queryKey" | "queryFn"
  >,
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplateJsonSchemaResponse, AxiosError>({
    queryKey: caseTemplatesKeys.dataSchema(
      wsId,
      templateId || "",
      versionId || "",
    ),
    queryFn: async () => {
      if (!apiClient || !templateId || !versionId) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.getCaseTemplateVersionDataJsonSchemaApiV1TemplatesTemplateIdVersionsVersionIdCaseDataJsonSchemaGet(
          { templateId, versionId },
        );
      return response.data;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled:
      !!apiClient && !!templateId && !!versionId && options?.enabled !== false,
  });
};

/** Imperative version of useCaseTemplateDataSchema that populates the cache. */
export const useEnsureCaseTemplateDataSchema = () => {
  const { wsId, apiClient } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useCallback(
    async (templateId: string, versionId: string) => {
      return queryClient.ensureQueryData({
        queryKey: caseTemplatesKeys.dataSchema(wsId, templateId, versionId),
        queryFn: async () => {
          if (!apiClient) {
            throw new Error("Missing required parameters");
          }

          const response =
            await apiClient.getCaseTemplateVersionDataJsonSchemaApiV1TemplatesTemplateIdVersionsVersionIdCaseDataJsonSchemaGet(
              { templateId, versionId },
            );
          return response.data;
        },
      });
    },
    [apiClient, queryClient, wsId],
  );
};

export type CaseJsonSchema =
  CaseTemplateJsonSchemaAttributes["case_json_schema"];

type CaseTemplateCaseSchemaForNode = {
  nodeId: string;
  caseJsonSchema: CaseJsonSchema;
};

const combineCaseTemplateCaseSchemasForNodes = (
  results: UseQueryResult<CaseTemplateCaseSchemaForNode, Error>[],
): Record<string, CaseJsonSchema | undefined> => {
  return results.reduce(
    (acc, result) => {
      if (result.data) {
        acc[result.data.nodeId] = result.data.caseJsonSchema;
      }
      return acc;
    },
    {} as Record<string, CaseJsonSchema | undefined>,
  );
};

/** Case json schema of each node's case template, keyed by node id. */
export const useCaseTemplateCaseSchemasForNodes = (
  nodes: CaseCreationNode[],
): Record<string, CaseJsonSchema | undefined> => {
  const { wsId, apiClient } = useWorkspaceParams();

  const validNodes = useMemo(
    () =>
      nodes.filter(
        (node) => node.data.template?.id && node.data.template?.version_id,
      ),
    [nodes],
  );

  return useQueries({
    queries: validNodes.map((node) => ({
      // Separate cache key - includes "forNode" to avoid conflict with useCaseTemplateDataSchema
      queryKey: [
        ...caseTemplatesKeys.dataSchema(
          wsId,
          node.data.template!.id,
          node.data.template!.version_id,
        ),
        "forNode",
        node.id,
      ],
      queryFn: async (): Promise<CaseTemplateCaseSchemaForNode> => {
        const response =
          await apiClient!.getCaseTemplateVersionDataJsonSchemaApiV1TemplatesTemplateIdVersionsVersionIdCaseDataJsonSchemaGet(
            {
              templateId: node.data.template!.id,
              versionId: node.data.template!.version_id,
            },
          );
        return {
          nodeId: node.id,
          caseJsonSchema: response.data.data.attributes.case_json_schema,
        };
      },
      staleTime: STALE_TIME,
      enabled: !!apiClient,
    })),
    combine: combineCaseTemplateCaseSchemasForNodes,
  });
};

export const useCaseTemplateRaw = <TData = CaseTemplateResponse>(
  templateId?: string | null,
  options?: Omit<CaseTemplateQueryOptions<TData>, "queryKey" | "queryFn">,
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplateResponse, AxiosError, TData>({
    queryKey: caseTemplatesKeys.get(wsId, templateId || ""),
    queryFn: async () => {
      if (!apiClient || !templateId) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.getCaseTemplateApiV1TemplatesTemplateIdGet({
          templateId,
        });
      return response.data;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled: !!apiClient && !!templateId && options?.enabled !== false,
  });
};

const getTemplateFromResponse = (data: CaseTemplateResponse) => {
  return data.data;
};

export const useCaseTemplate = (
  templateId: string | undefined,
  options?: Omit<
    CaseTemplateQueryOptions<CaseTemplate>,
    "queryKey" | "queryFn"
  >,
) => {
  return useCaseTemplateRaw(templateId, {
    select: getTemplateFromResponse,
    ...options,
  });
};

const linkVersionsToTemplate = (data: CaseTemplateResponse) => {
  const template = data.data;
  const versionRefs = template.relationships?.versions?.data;
  const templateVersions = Array.isArray(versionRefs)
    ? versionRefs.map((versionRef: Data) => versionRef.id)
    : [];

  return {
    ...template,
    versions: data.included?.filter((included) =>
      templateVersions.includes(included.id),
    ),
  };
};

export const useCaseTemplateWithVersions = (templateId: string | undefined) => {
  return useCaseTemplateRaw(templateId, {
    select: linkVersionsToTemplate,
  });
};

const deserializeCaseTemplate = (
  data: CaseTemplateResponse,
): DeserializedCaseTemplateWithVersions =>
  deserializeJsonApi<{ data: DeserializedCaseTemplateWithVersions }>(data).data;

export type CaseTemplateQueryResults = Array<
  UseQueryResult<DeserializedCaseTemplateWithVersions, Error>
>;

type UseCaseTemplatesByIdsOptions<TResult> = {
  combine?: (results: CaseTemplateQueryResults) => TResult;
};

export const useCaseTemplatesByIds = <TResult = CaseTemplateQueryResults>(
  templateIds: string[],
  options?: UseCaseTemplatesByIdsOptions<TResult>,
) => {
  const { wsId, apiClient } = useWorkspaceParams();

  return useQueries({
    queries: templateIds.map((templateId) => ({
      queryKey: caseTemplatesKeys.get(wsId, templateId),
      queryFn: async () => {
        if (!apiClient) {
          throw new Error(
            "Case management API client is not available. Please check workspace configuration.",
          );
        }

        const response =
          await apiClient.getCaseTemplateApiV1TemplatesTemplateIdGet({
            templateId,
          });

        return response.data;
      },
      select: deserializeCaseTemplate,
      staleTime: STALE_TIME,
      enabled: !!apiClient,
    })),
    ...(options?.combine ? { combine: options.combine } : {}),
  });
};

type CaseTemplateMetadataUpdateData = CaseTemplateMetadataUpdate & {
  id: string;
  etag: string | number;
};

type TemplateOptimisticUpdateContext = {
  previousTemplates?: CaseTemplatesResponse;
  previousTemplate?: CaseTemplate;
};

export const useCreateCaseTemplate = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTemplate,
    AxiosError,
    {
      template_name: string;
      description?: string;
      icon?: string;
    }
  >({
    mutationFn: async (templateData) => {
      if (!apiClient) {
        throw new Error(
          "Case management API client is not available. Please check workspace configuration.",
        );
      }

      const response = await apiClient.createCaseTemplateApiV1TemplatesPost({
        caseTemplateCreateRequest: {
          data: {
            type: "case_templates",
            attributes: {
              template_name: templateData.template_name,
              description: templateData.description,
              icon: templateData.icon,
            },
          },
        },
      });

      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
    },
  });
};

export const useUpdateCaseTemplate = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTemplate,
    AxiosError,
    CaseTemplateMetadataUpdateData,
    TemplateOptimisticUpdateContext
  >({
    mutationFn: async ({ id, etag, ...template }) => {
      if (!apiClient) {
        throw new Error(
          "Case management API client is not available. Please check workspace configuration.",
        );
      }

      const response =
        await apiClient.updateCaseTemplateMetadataApiV1TemplatesTemplateIdPut({
          templateId: id,
          caseTemplateMetadataUpdateRequest: {
            data: {
              type: "case_templates",
              attributes: {
                template_name: template.template_name,
                description: template.description,
                icon: template.icon,
              },
            },
          },
          ifMatch: String(etag),
        });

      return response.data.data;
    },
    onMutate: async ({ id, ...newData }) => {
      await queryClient.cancelQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
      await queryClient.cancelQueries({
        queryKey: caseTemplatesKeys.get(wsId, id),
      });

      const previousTemplates = queryClient.getQueryData<CaseTemplatesResponse>(
        caseTemplatesKeys.list(wsId),
      );
      const previousTemplate = queryClient.getQueryData<CaseTemplate>(
        caseTemplatesKeys.get(wsId, id),
      );

      if (previousTemplates) {
        queryClient.setQueryData<CaseTemplatesResponse>(
          caseTemplatesKeys.list(wsId),
          {
            ...previousTemplates,
            data: previousTemplates.data.map((template) =>
              template.id === id
                ? {
                    ...template,
                    attributes: {
                      ...template.attributes,
                      template_name: newData.template_name,
                      description: newData.description,
                      icon: newData.icon,
                    },
                  }
                : template,
            ),
          },
        );
      }

      if (previousTemplate) {
        queryClient.setQueryData<CaseTemplate>(
          caseTemplatesKeys.get(wsId, id),
          {
            ...previousTemplate,
            attributes: {
              ...previousTemplate.attributes,
              template_name: newData.template_name,
              description: newData.description,
              icon: newData.icon,
            },
          },
        );
      }

      return { previousTemplates, previousTemplate };
    },
    onError: (_, variables, context) => {
      if (context?.previousTemplates) {
        queryClient.setQueryData(
          caseTemplatesKeys.list(wsId),
          context.previousTemplates,
        );
      }
      if (context?.previousTemplate) {
        queryClient.setQueryData(
          caseTemplatesKeys.get(wsId, variables.id),
          context.previousTemplate,
        );
      }
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: caseTemplatesKeys.get(wsId, data.id),
        });
      }
    },
  });
};

type DuplicateTemplateForm = {
  templateId: string;
  versionId: string;
  versionName: string;
};

export const useDuplicateCaseTemplateVersion = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTemplateVersion,
    AxiosError,
    DuplicateTemplateForm,
    TemplateOptimisticUpdateContext
  >({
    mutationFn: async ({ templateId, versionId, versionName }) => {
      if (!apiClient) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.duplicateCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdDuplicatePost(
          {
            templateId,
            versionId,
            caseTemplateVersionDuplicateRequest: {
              data: {
                type: "case_template_versions",
                attributes: {
                  new_version_name: versionName,
                },
              },
            },
          },
        );

      const newVersion = response.data.data;

      const fileCategories = newVersion.attributes.file_categories || {};
      const hasOtherFiles = "other_files" in fileCategories;

      if (!hasOtherFiles) {
        // Add the default "Other Files" category
        const updateResponse =
          await apiClient.updateCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdPatch(
            {
              templateId,
              versionId: newVersion.id,
              caseTemplateVersionUpdateRequest: {
                data: {
                  type: "case_template_versions",
                  attributes: {
                    file_categories: {
                      ...fileCategories,
                      other_files: {
                        display_name: "Other Files",
                        description:
                          "Upload additional files supporting the case review.",
                      },
                    },
                  },
                },
              },
              ifMatch: String(newVersion.attributes.etag ?? 0),
            },
          );
        return updateResponse.data.data;
      }

      return newVersion;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.get(wsId, variables.templateId),
      });
    },
  });
};

export type CaseTemplateVersionWithBlocks = CaseTemplateVersion & {
  case_blocks_configs: Record<string, CaseBlockConfigOutput>;
};

export const useCaseTemplateVersion = <TData = CaseTemplateVersion>(
  params: {
    templateId: string | undefined;
    versionId: string | undefined;
  },
  options?: Omit<
    CaseTemplateVersionQueryOptions<TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const { templateId, versionId } = params;
  const { wsId, apiClient } = useWorkspaceParams();

  return useQuery<CaseTemplateVersion, AxiosError, TData>({
    queryKey: caseTemplatesKeys.version(
      wsId,
      templateId || "",
      versionId || "",
    ),
    queryFn: async () => {
      if (!apiClient || !templateId || !versionId) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.getCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdGet(
          { templateId, versionId },
        );

      return response.data.data;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled:
      !!apiClient && !!templateId && !!versionId && options?.enabled !== false,
  });
};

type UseCaseTemplateVersionForCaseParams = {
  templateId: string | undefined;
  versionId: string | undefined;
  /**
   * Etag of the pinned version as reported by the case's
   * `template_version_etag` attribute.
   */
  templateVersionEtag: string | undefined;
};

/**
 * Fetches the template version pinned to a case. The version embedded in the
 * case response can be stale (the case's composite etag does not cover it),
 * so the version is fetched directly and kept in sync with the case: whenever
 * the fetched version's etag stops matching the etag the case reports, the
 * query is invalidated so it refetches the fresh version.
 */
export const useCaseTemplateVersionForCase = (
  params: UseCaseTemplateVersionForCaseParams,
): UseQueryResult<CaseTemplateVersion, AxiosError> => {
  const { templateId, versionId, templateVersionEtag } = params;
  const { wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  const query = useCaseTemplateVersion({ templateId, versionId });
  const fetchedEtag = query.data?.attributes.etag;

  useEffect(() => {
    if (
      templateId &&
      versionId &&
      templateVersionEtag &&
      fetchedEtag &&
      fetchedEtag !== templateVersionEtag
    ) {
      void queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.version(wsId, templateId, versionId),
      });
    }
  }, [
    queryClient,
    wsId,
    templateId,
    versionId,
    templateVersionEtag,
    fetchedEtag,
  ]);

  return query;
};

type CaseTemplateVersionUpdateData = {
  templateId: string;
  versionId: string;
  versionAttributes: Partial<CaseTemplateVersionAttributes>;
  etag: string | number | undefined;
};

type TemplateVersionOptimisticUpdateContext = {
  previousVersion?: CaseTemplateVersion;
};

export const useUpdateCaseTemplateVersion = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTemplateVersion,
    AxiosError,
    CaseTemplateVersionUpdateData,
    TemplateVersionOptimisticUpdateContext
  >({
    mutationFn: async ({ templateId, versionId, etag, versionAttributes }) => {
      if (!apiClient) {
        throw new Error("Missing required parameters");
      }

      const response =
        await apiClient.updateCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdPatch(
          {
            templateId,
            versionId,
            caseTemplateVersionUpdateRequest: {
              data: {
                type: "case_template_versions",
                attributes: versionAttributes,
              },
            },
            ifMatch: String(etag ?? 0),
          },
        );

      return response.data.data;
    },
    onMutate: async ({ templateId, versionId, versionAttributes }) => {
      await queryClient.cancelQueries({
        queryKey: caseTemplatesKeys.version(wsId, templateId, versionId),
      });

      const previousVersion = queryClient.getQueryData<CaseTemplateVersion>(
        caseTemplatesKeys.version(wsId, templateId, versionId),
      );

      queryClient.setQueryData(
        caseTemplatesKeys.version(wsId, templateId, versionId),
        {
          ...(previousVersion ?? {}),
          attributes: {
            ...(previousVersion?.attributes ?? {}),
            ...versionAttributes,
          },
        },
      );

      return { previousVersion };
    },
    onError: (_, variables, context) => {
      if (context?.previousVersion) {
        queryClient.setQueryData(
          caseTemplatesKeys.version(
            wsId,
            variables.templateId,
            variables.versionId,
          ),
          context.previousVersion,
        );
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.get(wsId, variables.templateId),
      });
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.version(
          wsId,
          variables.templateId,
          variables.versionId,
        ),
      });
    },
  });
};

/**
 * Wraps useUpdateCaseTemplateVersion, transforming input into
 * CaseTemplateVersionUpdateData via the provided transform function.
 */
const useWrappedVersionMutation = <TInput>(
  transform: (input: TInput) => CaseTemplateVersionUpdateData,
) => {
  const {
    mutateAsync: updateAsync,
    mutate: update,
    ...mutation
  } = useUpdateCaseTemplateVersion();

  const transformRef = useRef(transform);
  transformRef.current = transform;

  const mutateAsync = useCallback(
    (input: TInput) => updateAsync(transformRef.current(input)),
    [updateAsync],
  );

  const mutate = useCallback(
    (input: TInput) => update(transformRef.current(input)),
    [update],
  );

  return { ...mutation, mutateAsync, mutate };
};

type PublishVersionInput = Omit<
  CaseTemplateVersionUpdateData,
  "versionAttributes"
> & { name: string };

export const usePublishCaseTemplateVersion = () =>
  useWrappedVersionMutation<PublishVersionInput>(({ name, ...data }) => ({
    ...data,
    versionAttributes: {
      publish_state: CaseTemplateVersionPublishState.PUBLISHED,
      version_name: name,
    },
  }));

type ArchiveVersionInput = Omit<
  CaseTemplateVersionUpdateData,
  "versionAttributes"
>;

export const useArchiveCaseTemplateVersion = () =>
  useWrappedVersionMutation<ArchiveVersionInput>((data) => ({
    ...data,
    versionAttributes: { archived: true },
  }));

export const useCaseTemplatesForWorkspace = (
  workspaceBaseUrl: string | undefined,
  options: { enabled?: boolean; authToken?: string } = { enabled: true },
) => {
  return useQuery({
    queryKey: options.authToken
      ? ["caseTemplates", "workspace", workspaceBaseUrl, "source-scoped"]
      : ["caseTemplates", "workspace", workspaceBaseUrl],
    queryFn: async () => {
      const apiClient = caseTemplatesApi(workspaceBaseUrl!);
      const response = await apiClient.getCaseTemplatesApiV1TemplatesGet(
        authTokenConfig(options.authToken),
      );
      return linkVersionsToTemplates(response.data);
    },
    enabled: !!workspaceBaseUrl && (options.enabled ?? true),
    staleTime: STALE_TIME,
  });
};

export const useCaseTemplateVersionFromWorkspace = (
  workspaceBaseUrl: string | undefined,
  templateId: string | undefined,
  versionId: string | undefined,
  options: { enabled?: boolean; authToken?: string } = { enabled: true },
) => {
  return useQuery<CaseTemplateVersion, AxiosError>({
    queryKey: [
      "caseTemplates",
      "workspace",
      workspaceBaseUrl,
      templateId,
      versionId,
      ...(options.authToken ? (["source-scoped"] as const) : []),
    ],
    queryFn: async () => {
      const apiClient = caseTemplatesApi(workspaceBaseUrl!);
      const response =
        await apiClient.getCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdGet(
          { templateId: templateId!, versionId: versionId! },
          authTokenConfig(options.authToken),
        );
      return response.data.data;
    },
    enabled:
      !!workspaceBaseUrl &&
      !!templateId &&
      !!versionId &&
      (options.enabled ?? true),
    staleTime: STALE_TIME,
  });
};

export const useCreateTemplateFromExisting = () => {
  const { apiClient, wsId } = useWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    { template: CaseTemplate; version: CaseTemplateVersion },
    AxiosError,
    {
      templateName: string;
      templateDescription?: string;
      templateIcon?: string;
      transformedVersionData: Partial<CaseTemplateVersionAttributes>;
    }
  >({
    mutationFn: async ({
      templateName,
      templateDescription,
      templateIcon,
      transformedVersionData,
    }) => {
      if (!apiClient) {
        throw new Error(
          "Case management API client is not available. Please check workspace configuration.",
        );
      }

      // Create template
      const { data } = await apiClient.createCaseTemplateApiV1TemplatesPost({
        caseTemplateCreateRequest: {
          data: {
            type: "case_templates",
            attributes: {
              template_name: templateName,
              description: templateDescription,
              icon: templateIcon,
            },
          },
        },
      });

      const templateId = data.data.id;

      // Get default version
      const templateResponse =
        await apiClient.getCaseTemplateApiV1TemplatesTemplateIdGet({
          templateId,
        });

      const defaultVersion = templateResponse.data.included?.find(
        (item) => item.type === "case_template_versions",
      );

      if (!defaultVersion) {
        throw new Error("No default version found");
      }

      // Update with transformed data
      const versionResponse =
        await apiClient.updateCaseTemplateVersionApiV1TemplatesTemplateIdVersionsVersionIdPatch(
          {
            templateId,
            versionId: defaultVersion.id,
            caseTemplateVersionUpdateRequest: {
              data: {
                type: "case_template_versions",
                attributes: transformedVersionData,
              },
            },
            ifMatch: String(defaultVersion.attributes.etag ?? 0),
          },
        );

      return { template: data.data, version: versionResponse.data.data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: caseTemplatesKeys.list(wsId),
      });
    },
  });
};
